import { ChangeDetectionStrategy, Component, ElementRef, effect, input, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { VoiceMicInputComponent } from '@components/voice-mic-input/voice-mic-input.component';
import { CsIconModule } from '@components/icon';
import { appendTranscript, scrollTextFieldToEnd } from '@services/voice-input-service/text-field.utils';

const VOICE_MIC_SELECTOR = 'voice-mic-input';

@Component({
  selector: 'app-quiz-text-answer',
  templateUrl: './quiz-text-answer.html',
  standalone: true,
  imports: [FormsModule, ButtonCtaComponent, VoiceMicInputComponent, CsIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuizTextAnswerComponent {
  readonly placeholder = input<string>('');
  readonly disabled = input<boolean>(false);
  readonly showVoice = input<boolean>(false);
  /** Text already answered, so returning to a question by pressing back shows what was written. */
  readonly value = input<string | string[] | null>(null);
  readonly valueChange = output<string>();

  readonly draft = signal<string>('');
  private readonly micInput = viewChild(VoiceMicInputComponent);
  private readonly answerTextarea = viewChild<ElementRef<HTMLTextAreaElement>>('answerTextarea');

  constructor() {
    effect(() => {
      this.placeholder();
      const answered = this.value();
      this.draft.set((Array.isArray(answered) ? answered.join(', ') : answered) || '');
    });
  }

  onDraftChange(value: string): void {
    this.draft.set(value);
    this.valueChange.emit(value);
  }

  onTapToSpeak(event: MouseEvent): void {
    // clicks on the mic itself are handled by the mic component — avoid a double listen()
    if ((event.target as HTMLElement).closest(VOICE_MIC_SELECTOR)) return;
    this.micInput()?.onClick();
  }

  onVoiceTranscript(transcript: string): void {
    this.onDraftChange(appendTranscript(this.draft(), transcript));
    // ngModel only writes the DOM value on the next tick — scroll once it is actually there.
    setTimeout((): void => scrollTextFieldToEnd(this.answerTextarea()?.nativeElement));
  }
}
